import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Container,
  Paper,
} from '@mui/material';
import { AdminPanelSettings as AdminPanelSettingsIcon } from '@mui/icons-material';
import api from '../utils/api';
import { useAuth } from './auth/AuthContext';

const GovernmentLogin = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleEmailSubmit = async () => {
    if (!email) {
      setError('Please enter your email');
      return;
    }
    setLoading(true);
    try {
      await api.post('/api/schools/admin/request-code', { email });
      setStep(2);
      setError(null);
    } catch (error) {
      setError(
        error.response?.data?.message ||
          error.response?.data?.error ||
          'Failed to send verification code'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCodeSubmit = async () => {
    if (!code || code.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }
    setLoading(true);
    try {
      const response = await api.post('/api/schools/admin/verify-code', { email, code });
      if (response.data.message === 'Admin authentication successful') {
        await login(response.data.token);
        navigate('/admin-dashboard');
      } else {
        setError(response.data.message || 'Verification failed');
      }
    } catch (error) {
      setError(
        error.response?.data?.message ||
          `Server error: ${error.response?.status}` ||
          'Verification failed'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container
      maxWidth="sm"
      sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', bgcolor: '#f8f9fa' }}
    >
      <Paper elevation={3} sx={{ p: 4, width: '100%', borderRadius: 2, textAlign: 'center' }} data-aos="fade-up">
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <AdminPanelSettingsIcon sx={{ fontSize: 80, color: '#9C27B0' }} />
        </Box>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2c3e50', mb: 2 }}>
          Authorized Creator Admin Portal
        </Typography>
        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}
        {step === 1 ? (
          <>
            <Typography variant="subtitle1" sx={{ color: '#666', mb: 3 }}>
              Enter the official registered email to receive a verification code
            </Typography>
            <TextField
              fullWidth
              label="Official Admin Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              sx={{ mb: 3 }}
              disabled={loading}
            />
            <Button
              fullWidth
              variant="contained"
              onClick={handleEmailSubmit}
              disabled={loading}
              sx={{ bgcolor: '#9C27B0', '&:hover': { bgcolor: '#7B1FA2' }, py: 1.5, borderRadius: 1 }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Send Verification Code'}
            </Button>
          </>
        ) : (
          <>
            <Typography variant="subtitle1" sx={{ color: '#666', mb: 2 }}>
              A 6-digit code has been sent to {email}
            </Typography>
            <Typography variant="subtitle1" sx={{ color: '#666', mb: 3 }}>
              Enter the code below (valid for 5 minutes)
            </Typography>
            <TextField
              fullWidth
              label="6-digit Code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              type="number"
              inputProps={{ maxLength: 6 }}
              sx={{ mb: 3 }}
              disabled={loading}
            />
            <Button
              fullWidth
              variant="contained"
              onClick={handleCodeSubmit}
              disabled={loading}
              sx={{ bgcolor: '#9C27B0', '&:hover': { bgcolor: '#7B1FA2' }, py: 1.5, borderRadius: 1 }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify Code'}
            </Button>
            <Button
              variant="text"
              onClick={() => setStep(1)}
              sx={{ mt: 2, color: '#9C27B0' }}
            >
              Back to email entry
            </Button>
          </>
        )}
      </Paper>
    </Container>
  );
};

export default GovernmentLogin;